
<template>
    <div class="d-none" id="shippingFormInitializer"></div>
</template>

<script setup>
import { mapGetters, mapMutations } from "@/store/helper/vuex";
import { onMounted, computed, markRaw, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { mergeDynamicForm } from "@/components/helper/util"

import EmailInject from '@/components/checkout/injectComponents/EmailInject.vue';
import PhoneInject from '@/components/checkout/injectComponents/PhoneInject.vue';
import SaveAddressInject from "@/components/checkout/injectComponents/SaveAddressInject.vue";
import AddressSuggestions from "@/components/checkout/injectComponents/AddressSuggestions.vue";
import DaumFindInject from "@/components/checkout/injectComponents/DaumFindInject.vue";
import DaumCloseInject from "@/components/checkout/injectComponents/DaumCloseInject.vue";

const { t } = useI18n();

const { checkout, formFields, lastFormData, currentStage, vueVariables, shippingDynamicStructure, shippingVars } = mapGetters();
const { setShippingDynamicStructure, setShippingLocale } = mapMutations();

const pdict = checkout || mapGetters().pdict;

const pdictExists = computed(() => { return Object.values(pdict.value).length === 0 ? false : true });
const isCustomerAuthenticated = computed(() => { return vueVariables.value.isCustomerAuthenticated });
const shippingModel = computed(() => { return pdict.value.order ? pdict.value.order.shipping[0] : {} });
const getVisibleOnActiveStageInject = computed(() => { return currentStage.value === 'shipping' });
const selectedShippingAddressUUID = computed(() => { return shippingVars ? shippingVars.value.selectedAddressUUID : pdict.value.selectedAddressUUID});
const selectedShippingAddress = computed(() => { return selectedShippingAddressUUID.value ? pdict.value.addresses.find((address) => { return address.UUID === selectedShippingAddressUUID.value; }) : null });

const initShippingForm = () => {
    const shippingCountryCode = computed(() => { return formFields.value['shipping'].country || pdict.value.countryCode });
    const isNonDefaultEUCountry = computed(() => { return pdict.value.euCountryCodeList.includes(shippingCountryCode.value) && shippingCountryCode.value !== 'FR' });

    const shippingLocale = computed(() => {
        let dynamicFormShippingStructureJson = pdict.value.dynamicForm.dynamicFormShippingStructureJson;
        const dynamicFormShippingStructureJsonKeys = Object.keys(dynamicFormShippingStructureJson);
        let computedLocale;

        if (pdict.value.locale.includes(shippingCountryCode.value)) {
            computedLocale = shippingCountryCode.value === 'US' ? 'default' : pdict.value.locale;
        } else {
            let countryCodeKey = dynamicFormShippingStructureJsonKeys.find(key => key.includes(shippingCountryCode.value));
            if (isNonDefaultEUCountry.value && !countryCodeKey) {
                countryCodeKey = dynamicFormShippingStructureJsonKeys.find(key => key.includes('en_FR'));
            }
            computedLocale = countryCodeKey ? countryCodeKey : 'default';
        }

        return computedLocale;
    });

    setShippingLocale(shippingLocale);

    let dynamicFormStructure = computed(() => { return shippingDynamicStructure.value[shippingLocale.value] ? shippingDynamicStructure.value[shippingLocale.value] : pdict.value.dynamicForm.dynamicFormShippingStructureJson[shippingLocale.value] });

    const formDataFields = computed(() => lastFormData.value['shipping']);

    const prepareShippingForm = () => {

        const modelShippingAddress = computed(() => { return shippingModel.value.shippingAddress ? shippingModel.value.shippingAddress : {} });

        const { FRESH_addressLineCharLimit } = window.vue.customPreferences

        const selectedCountryCode = computed(() => {
            return formDataFields.value.country.options.find((country) => {
                return (modelShippingAddress.value.countryCode && modelShippingAddress.value.countryCode.value || pdict.value.countryCode) === country.id
            })
        })

        let finalAdditionalProperties = {};


        const selectedStateCode = computed(() => {
            return formDataFields.value.stateCode && formDataFields.value.stateCode.options && formDataFields.value.stateCode.options.find((state) => {
                return state.id === modelShippingAddress.value.stateCode
            })
        })


        const selectedAreaCode = computed(() => {
            return formDataFields.value.areaCode && formDataFields.value.areaCode.options ? formDataFields.value.areaCode.options.find((areaCode, index) => {
                return selectedShippingAddress.value ? selectedShippingAddress.value.areaCode === areaCode.htmlValue : index === 0;
            }) : null
        })

        if (pdict.value.locale === 'zh_HK' || pdict.value.locale === 'en_HK') {

            const selectedStateCode = computed(() => {
                return formDataFields.value.stateCode && formDataFields.value.stateCode.options && formDataFields.value.stateCode.options.find((state) => {
                    return formFields.value['shipping'].stateCode !== undefined ? formFields.value['shipping'].stateCode === state.id : state.id === modelShippingAddress.value.stateCode
                })
            })

            const cityFilteredOptions = computed(() => {
                return (selectedStateCode.value && selectedStateCode.value.id) ? formDataFields.value.city.options.filter((district) => {
                    return district.id.includes(selectedStateCode.value.id);
                }) : [formDataFields.value.city.options[0], {
                    ...formDataFields.value.city.options.find((district) => formFields.value['shipping'].city === district.htmlValue),
                    hidden: true
                }]
            });

            const selectedShippingCityDistrict = computed(() => {
                return cityFilteredOptions.value.find((district) => {
                    return formFields.value['shipping'].city !== undefined ? cityFilteredOptions.value[0].htmlValue === district.htmlValue : modelShippingAddress.value.city === district.htmlValue
                })
            });

            const selectedAreaCode = computed(() => {
                return formDataFields.value.postalCode.options.find((areaCode, index) => {
                    return selectedShippingAddress.value ? selectedShippingAddress.value.postalCode === areaCode.htmlValue : index === 0;
                })
            });

            finalAdditionalProperties = {
                stateCode: {
                    defaultValue: computed(() => { return selectedStateCode.value ? selectedStateCode.value.id : '' }),
                    events: {
                        input: true,
                        change: 'updateHKShippingDistrictsOptions'
                    },
                },
                city: {
                    defaultValue: computed(() => { return selectedShippingCityDistrict.value ? selectedShippingCityDistrict.value.htmlValue : '' }),
                    filteredOptions: computed(() => cityFilteredOptions.value),
                    events: {
                        input: true,
                        change: 'updateShippingMethodDynamicFields'
                    },
                    tag: "select",
                    formGroupClasses: "checkout-select",
                    autocomplete: "shipping address-level1"
                },
                postalCode: {
                    defaultValue: computed(() => { return selectedAreaCode.value ? selectedAreaCode.value.id : formDataFields.value.postalCode.options[0].id }),
                    events: {
                        input: true,
                        change: 'updateShippingMethodDynamicFields'
                    },
                    tag: "select",
                    formGroupClasses: "checkout-select field-areacode",
                    columnLayoutClasses: "col-sm-3"
                },
                phone: {
                    columnLayoutClasses: "col-sm-9"
                }
            }

        }

        let defaultAdditionalProperties = {
            addressId: {
                visible: isCustomerAuthenticated,
                defaultValue: computed(() => { return pdict.value.shipmentAddressID || '' }),
                events: {
                    input: true
                },
                classes: "addressId",
                formId: "addressId"
            },
            firstName: {
                defaultValue: computed(() => { return modelShippingAddress.value.firstName || '' }),
                events: {
                    input: true
                },
                classes: "shippingFirstName",
                formId: "shippingFirstName"
            },
            lastName: {
                defaultValue: computed(() => { return modelShippingAddress.value.lastName || '' }),
                events: {
                    input: true
                },
                classes: "shippingLastName",
                formId: "shippingLastName"
            },
            address1: {
                attributes: {
                    maxLength: FRESH_addressLineCharLimit || 30,
                },
                defaultValue: computed(() => { return modelShippingAddress.value.address1 || '' }),
                events: {
                    input: true,
                    change: 'updateShippingMethodDynamicFields'
                },
                formId: "shippingAddressOne",
                classes: "shippingAddressOne",
                autocomplete: "shipping address-line1"
            },
            address2: {
                defaultValue: computed(() => { return modelShippingAddress.value.address2 || '' }),
                events: {
                    input: true,
                    change: 'updateShippingMethodDynamicFields'
                },
                formId: "shippingAddressTwo",
                classes: "shippingAddressTwo",
                autocomplete: "shipping address-line2"
            },
            country: {
                defaultValue: computed(() => { return selectedCountryCode.value ? selectedCountryCode.value.id : '' }),
                events: {
                    input: true,
                    change: "updateShippingMethodDynamicFields"
                },
                tag: "select",
                formGroupClasses: "checkout-select",
                classes: "shippingCountry",
                formId: "shippingCountry",
                autocomplete: "shipping country"
            },
            postalCode: {
                classes: 'shippingZipCode',
                type: formDataFields.value.postalCode ? formDataFields.value.postalCode.inputType : 'text',
                defaultValue: computed(() => { return modelShippingAddress.value.postalCode || '' }),
                events: {
                    input: true,
                    change: 'updateShippingMethodDynamicFields'
                },
                type: 'tel',
                formId: "shippingZipCode",
                autocomplete: "shipping postal-code"
            },
            stateCode: {
                defaultValue: computed(() => { return selectedStateCode.value ? selectedStateCode.value.id : '' }),
                events: {
                    input: true,
                    change: 'updateShippingMethodDynamicFields'
                },
                tag: "select",
                formId: "shippingState",
                formGroupClasses: "checkout-select",
                classes: "shippingState",
                autocomplete: "shipping address-level1"
            },
            city: {
                defaultValue: computed(() => { return modelShippingAddress.value.city || '' }),
                events: {
                    input: true,
                    change: 'updateShippingMethodDynamicFields'
                },
                formId: "shippingAddressCity",
                classes: "shippingAddressCity",
                autocomplete: "shipping address-level2"
            },
            email: {
                visible: computed(() => { return !(isCustomerAuthenticated.value) }),
                defaultValue: computed(() => { return pdict.value.customerEmail || '' }),
                events: {
                    input: true,
                    focus: "shippingFormToggleEmailDetails",
                    click: "shippingFormToggleEmailDetails",
                    blur: "emailValidationBlur"
                },
                formId: "shippingEmail",
                classes: "email toggle-details",
                hasNoColumnClass: true
            },
            areaCode: {
                visible: formDataFields.value.areaCode ? true : false,
                defaultValue: computed(() => { return selectedAreaCode.value ? selectedAreaCode.value.id : '' }),
                events: {
                    input: true
                },
                tag: "select",
                formId: "shippingAreaCode",
                columnLayoutClasses: "col-sm-3",
                formGroupClasses: "checkout-select field-areacode",
                classes: "shippingAreaCode"
            },
            phone: {
                legend: {
                    text: t('label.phone.note'),
                    classes: 'mt-1 mb-0 body-smallest',
                },
                columnLayoutClasses: formDataFields.value.areaCode ? 'col-sm-9' : 'col-12',
                defaultValue: computed(() => { return modelShippingAddress.value.phone || '' }),
                events: {
                    input: true,
                    focus: "shippingFormTogglePhoneDetails",
                    click: "shippingFormTogglePhoneDetails",
                    blur: "phoneValidationPhoneBlur"
                },
                formId: "shippingPhoneNumber",
                classes: "shippingPhoneNumber",
                type: "tel",
                autocomplete: "tel",
                attributes: {
                    maxLength: 20,
                    minLength: 0
                }
            },
            "phone-inject": {
                classInstance: markRaw(PhoneInject),
                type: 'inject',
            },
            "save-address-inject": {
                classInstance: markRaw(SaveAddressInject),
                visible: getVisibleOnActiveStageInject,
                type: 'inject',
            },
            "address-suggestion-inject": {
                classInstance: markRaw(AddressSuggestions),
                type: 'inject',
            },
            "email-inject": {
                classInstance: markRaw(EmailInject),
                type: 'inject',
            },
            'daum-find-inject': {
                classInstance: markRaw(DaumFindInject),
            },
            'daum-close-inject': {
                classInstance: markRaw(DaumCloseInject),
            },
        };

        Object.keys(finalAdditionalProperties).length ? mergeDynamicForm(finalAdditionalProperties, defaultAdditionalProperties, { mergeModeSupport: true }) : (finalAdditionalProperties = defaultAdditionalProperties);

        mergeDynamicForm(finalAdditionalProperties, formDataFields.value, { mergeModeSupport: true });

        for (const rowKey of Object.keys(dynamicFormStructure.value)) {
            for (let field of dynamicFormStructure.value[rowKey].fields) {
                if (finalAdditionalProperties[field.name]) {
                    mergeDynamicForm(field, finalAdditionalProperties[field.name], { mergeModeSupport: true });
                }
            }
        }

        dynamicFormStructure.value.get = function (key) {
            return Object.values(this).flatMap(row => row.fields).find(field => field ? field.name === key : null);
        };

        setShippingDynamicStructure({ locale: shippingLocale.value, value: dynamicFormStructure.value });
    }

    prepareShippingForm();

    const refreshShippingForm = watch(shippingLocale, (newShippingLocale, oldShippingLocale) => {

        if (newShippingLocale !== oldShippingLocale) {
            if (!shippingDynamicStructure.value[newShippingLocale]) {
                prepareShippingForm();
            } else {
                setShippingDynamicStructure({ locale: newShippingLocale, value: dynamicFormStructure.value });
            }
        }

    }, {
        flush: 'post'
    });
}

onMounted(() => {
    if (pdictExists.value) {
        initShippingForm();
    } else {
        const initShippingFormWatcher = watch(pdictExists, (newVal) => {
            if (newVal === true) {
                initShippingForm();
                initShippingFormWatcher();
            }
        })
    }
})

</script>